// src/hooks/useAPI.ts

import { useState, useEffect, useCallback, useRef } from 'react';

// Global cache to prevent duplicate requests
const requestCache = new Map<string, Promise<any>>();
const cacheExpiry = new Map<string, number>();
const CACHE_DURATION = 30000; // 30 seconds

// Request queue for sequential processing
const requestQueue: Array<() => void> = [];
let isProcessingQueue = false;

function getCacheKey(apiCall: () => Promise<any>): string {
  // Simple cache key based on function name - in production, you'd want better key generation
  return apiCall.toString().slice(0, 100);
}

async function processQueue() {
  if (isProcessingQueue || requestQueue.length === 0) return;

  isProcessingQueue = true;

  while (requestQueue.length > 0) {
    const resolve = requestQueue.shift()!;
    resolve();

    // Add delay between requests
    await new Promise(resolve => setTimeout(resolve, 150));
  }

  isProcessingQueue = false;
}

async function throttleRequest<T>(apiCall: () => Promise<T>): Promise<T> {
  return new Promise((resolve) => {
    requestQueue.push(async () => {
      try {
        const result = await apiCall();
        resolve(result);
      } catch (error) {
        resolve(Promise.reject(error));
      }
    });

    processQueue();
  });
}

interface UseAPIResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAPI<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = [],
  cacheKey?: string
): UseAPIResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const key = cacheKey || getCacheKey(apiCall);

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && cacheExpiry.get(key) && Date.now() < cacheExpiry.get(key)!) {
        const cachedData = requestCache.get(key);
        if (cachedData) {
          setData(await cachedData);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);

      // Throttle the request
      const result = await throttleRequest(apiCall);

      if (mountedRef.current) {
        setData(result);
        // Cache the result
        requestCache.set(key, Promise.resolve(result));
        cacheExpiry.set(key, Date.now() + CACHE_DURATION);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err.message || 'An error occurred');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiCall, key]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, ...dependencies]);

  const refetch = useCallback(async () => {
    // Clear cache for this key
    requestCache.delete(key);
    cacheExpiry.delete(key);
    await fetchData(true);
  }, [fetchData, key]);

  return { data, loading, error, refetch };
}
